// ---------------------------------------------------------
// Datei: hubspotBackendClient.ts
// Zweck: Service-Layer für HubSpot Backend-Kommunikation
// Besonderheiten:
// - Nutzt die zentrale API-Instanz aus api.ts
// - Type-Safe mit TypeScript
// - Fehlerbehandlung durch Axios-Interceptors
// - Kombination von Upload + Patch in einer Funktion
// ---------------------------------------------------------

import api from './api';
import {
  HubSpotDealsResponse,
  HubSpotDeal,
  HubSpotUploadResponse,
  HubSpotSignedUrlResponse,
  NacharbeitenStatusSnapshot,
  NacharbeitenTicketListItem,
  DealContactListItem,
} from '../types/hubspot';

const DEALS_PAGE_TIMEOUT_MS = 20_000;
const DEALS_PAGE_TIMEOUT_RETRIES = 1;
const DEALS_SEARCH_TIMEOUT_MS = 65_000;
const DEALS_SEARCH_TIMEOUT_RETRIES = 0;

function isTimeoutLikeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return message.includes('timeout') || message.includes('timed out') || message.includes('aborted');
}

function isAbortLikeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return message.includes('abort') || message.includes('canceled') || message.includes('cancelled');
}

// === SICHERE Route: Default-Deals ohne Parameter ===
// Dealstage wird INTERN vom Backend verwaltet (env-Variable)
// Diese Funktion hat KEINE dealstage-Parameter → dealstage ist verschleiert!
//
// Warum diese neue Funktion?
// - Sicherheit: Interne HubSpot-IDs bleiben im Backend (nicht im Frontend!)
// - Wartung: dealstage zentral über env-Variable konfigurierbar
// - Best-Practice: Frontend konsumiert nur API-Daten, Backend verwaltet Konfiguration
//
// Use-Case:
//   Frontend ruft auf: const deals = await getDefaultDeals()
//   Backend-Request: GET /api/hubspot/deals/default (KEINE Query-Parameter!)
//   Resultat: Gleiche Deal-Liste wie getDealsByDealstage('3259140292')
export async function getDefaultDeals(options?: {
  refresh?: boolean;
  refreshNacharbeiten?: boolean;
  offset?: number;
  limit?: number;
  q?: string;
  stageLabel?: string;
  nacharbeiten?: 'all' | 'yes' | 'no';
  signal?: AbortSignal;
}): Promise<HubSpotDealsResponse> {
  const isSearchRequest = Boolean(options?.q && options.q.trim().length >= 3);
  const timeoutMs = isSearchRequest ? DEALS_SEARCH_TIMEOUT_MS : DEALS_PAGE_TIMEOUT_MS;
  const timeoutRetries = isSearchRequest ? DEALS_SEARCH_TIMEOUT_RETRIES : DEALS_PAGE_TIMEOUT_RETRIES;

  const params = new URLSearchParams();
  if (options?.refresh) params.set('refresh', '1');
  if (options?.refreshNacharbeiten) params.set('refreshNacharbeiten', '1');
  if (typeof options?.offset === 'number') params.set('offset', String(options.offset));
  if (typeof options?.limit === 'number') params.set('limit', String(options.limit));
  if (options?.q && options.q.trim().length > 0) params.set('q', options.q.trim());
  if (options?.stageLabel && options.stageLabel.trim().length > 0) params.set('stageLabel', options.stageLabel.trim());
  if (options?.nacharbeiten === 'yes' || options?.nacharbeiten === 'no') params.set('nacharbeiten', options.nacharbeiten);
  const qs = params.toString();
  const url = `/hubspot/deals/default${qs ? `?${qs}` : ''}`;

  let lastError: unknown = null;
  for (let attempt = 0; attempt <= timeoutRetries; attempt += 1) {
    try {
      return await api.get<HubSpotDealsResponse>(url, {
        timeout: timeoutMs,
        signal: options?.signal,
      });
    } catch (error) {
      if (isAbortLikeError(error)) {
        throw error;
      }
      lastError = error;
      if (!isTimeoutLikeError(error) || attempt >= timeoutRetries) {
        break;
      }
      console.warn('[HubSpotDeals] Timeout beim Laden, retry', {
        url,
        attempt: attempt + 1,
      });
    }
  }

  try {
    throw lastError;
  } catch (error) {
    console.error('Fehler beim Laden der Default-Deals:', error);
    throw new Error('Deals konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
  }
}

export async function searchDeals(query: string, options?: { signal?: AbortSignal }): Promise<HubSpotDeal[]> {
  try {
    const q = query.trim();
    if (q.length < 3) return [];
    const response = await api.get<HubSpotDealsResponse>(
      `/hubspot/search?q=${encodeURIComponent(q)}`,
      { signal: options?.signal }
    );
    return response.deals;
  } catch (error) {
    if (isAbortLikeError(error)) {
      throw error;
    }
    console.error('Fehler bei der Deal-Suche:', error);
    throw new Error('Deal-Suche fehlgeschlagen.');
  }
}

export async function resolveHubSpotOwnerIdsByEmail(email: string): Promise<string[]> {
  try {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) return [];
    const response = await api.get<{
      ownerIds?: string[];
    }>(`/hubspot/owners/resolve`, {
      params: { email: normalizedEmail },
    });

    const ownerIds = Array.isArray(response.ownerIds) ? response.ownerIds : [];
    return ownerIds
      .map((ownerId) => String(ownerId || '').trim())
      .filter(Boolean);
  } catch (error) {
    console.error('Fehler beim Auflösen der HubSpot Owner-IDs:', error);
    return [];
  }
}

export async function getDealById(dealId: string): Promise<HubSpotDeal | null> {
  try {
    const id = dealId.trim();
    if (!id) return null;
    const response = await api.get<HubSpotDealsResponse>(
      `/hubspot/search?ids=${encodeURIComponent(id)}`
    );
    const deals = response.deals || [];
    return deals.find((deal) => deal.id === id) || null;
  } catch (error) {
    console.error("Fehler beim Laden des Deals per ID:", error);
    return null;
  }
}

export async function getDealContacts(dealId: string): Promise<DealContactListItem[]> {
  try {
    const id = dealId.trim();
    if (!id) return [];
    const response = await api.get<{ contacts?: DealContactListItem[] }>(
      `/hubspot/deal/${encodeURIComponent(id)}/contacts`
    );
    return Array.isArray(response.contacts) ? response.contacts : [];
  } catch (error) {
    console.error("Fehler beim Laden der Deal-Kontakte:", error);
    return [];
  }
}

export async function getNacharbeitenStatus(projectId: string): Promise<NacharbeitenStatusSnapshot> {
  try {
    const response = await api.get<{ nacharbeiten: NacharbeitenStatusSnapshot }>(
      `/projects/${encodeURIComponent(projectId)}/nacharbeiten-status`
    );

    return response.nacharbeiten;
  } catch (error) {
    console.error('Fehler beim Laden des Nacharbeiten-Status:', error);
    throw new Error('Nacharbeiten-Status konnte nicht geladen werden.');
  }
}

export async function getNacharbeitenTickets(projectId: string): Promise<NacharbeitenTicketListItem[]> {
  try {
    const response = await api.get<{
      debug?: {
        allTickets?: NacharbeitenTicketListItem[];
      } | null;
    }>(
      `/projects/${encodeURIComponent(projectId)}/nacharbeiten-status?debug=1`
    );

    return response?.debug?.allTickets || [];
  } catch (error) {
    console.error('Fehler beim Laden der Nacharbeiten-Tickets:', error);
    throw new Error('Nacharbeiten-Tickets konnten nicht geladen werden.');
  }
}

// === Deals nach Dealstage laden ===
// Ruft den Backend-Endpunkt GET /api/hubspot/search auf
// und liefert eine Liste von Deals zurück.
//
// ⚠️ DEPRECATED: Diese Funktion sollte NICHT mehr verwendet werden!
// BITTE STATTDESSEN: getDefaultDeals() verwenden (sicherer!)
//
// Parameter:
// - dealstageId: HubSpot Dealstage ID (z.B. "3259140292" für Test-Deals)
//
// Return:
// - Array von HubSpotDeal-Objekten
//
// Fehlerbehandlung:
// - Axios-Interceptors aus api.ts fangen Fehler ab
// - 401: Token ungültig → automatisch logout
// - Andere Fehler: Exception mit aussagekräftiger Message
export async function getDealsByDealstage(
  dealstageId: string
): Promise<HubSpotDeal[]> {
  try {
    // API-Call: GET /api/hubspot/search?dealstage=<ID>
    const response = await api.get<HubSpotDealsResponse>(
      `/hubspot/search?dealstage=${dealstageId}`
    );

    // Response ist bereits durch Interceptor ausgepackt (response.data)
    return response.deals;
  } catch (error) {
    console.error('Fehler beim Laden der Deals:', error);
    throw new Error('Deals konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
  }
}

// === Signed URL für FileID abrufen ===
// Ruft den Backend-Endpunkt GET /api/hubspot/file/:fileId auf
// und liefert eine 24h-gültige signed URL zurück.
//
// Parameter:
// - fileId: HubSpot FileID (z.B. "123456789")
//
// Return:
// - Signed URL als String (direkt öffenbar in neuem Tab)
//
// Use-Case:
// - User klickt auf "Ansehen"-Button
// - Frontend holt signed URL
// - window.open(url, '_blank') öffnet Datei
//
// Wichtig:
// - Signed URLs sind nur 24h gültig
// - Kein Caching empfohlen (URLs können ablaufen)
export async function getSignedUrl(fileId: string): Promise<string> {
  try {
    // API-Call: GET /api/hubspot/file/:fileId
    const response = await api.get<HubSpotSignedUrlResponse>(
      `/hubspot/file/${fileId}`
    );

    return response.url;
  } catch (error) {
    console.error('Fehler beim Abrufen der signed URL:', error);
    throw new Error('Die Datei konnte nicht abgerufen werden. Bitte versuchen Sie es später erneut.');
  }
}

// === Datei hochladen und Deal patchen (kombiniert) ===
// Diese Funktion kombiniert zwei Backend-Calls:
// 1. POST /api/hubspot/upload → Upload der Datei zu HubSpot
// 2. PATCH /api/hubspot/deal → Speichern der FileID im Deal-Property
//
// Warum kombiniert?
// - Atomare Operation aus Sicht der UI
// - Fehlerbehandlung zentralisiert
// - Weniger Boilerplate-Code in Komponenten
//
// Parameter:
// - dealId: HubSpot Deal ID (z.B. "313739230424")
// - propertyName: Technischer Property-Name (z.B. "heizlastberechnung")
// - file: File-Objekt aus <input type="file">
//
// Return:
// - Die neue FileID (zur Aktualisierung der UI)
//
// Fehlerbehandlung:
// - Wenn Upload fehlschlägt → Exception (Patch wird nicht ausgeführt)
// - Wenn Patch fehlschlägt → Exception (aber Datei ist bereits in HubSpot!)
//   → TODO: Rollback-Logik für Produktiv-System erwägen
export async function uploadAndPatchDeal(
  dealId: string,
  propertyName: string,
  file: File
): Promise<string> {
  try {
    // === Schritt 1: Datei hochladen ===
    // FormData für Multipart-Upload erstellen
    const formData = new FormData();
    formData.append('file', file);

    // POST /api/hubspot/upload
    const uploadResponse = await api.post<HubSpotUploadResponse>(
      '/hubspot/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    const fileId = uploadResponse.fileId;

    // === Schritt 2: FileID in Deal patchen ===
    // PATCH /api/hubspot/deal
    await api.patch('/hubspot/deal', {
      dealId,
      fileId,
      property: propertyName
    });

    // === Erfolg: FileID zurückgeben ===
    return fileId;
  } catch (error) {
    console.error('Fehler beim Upload und Patch:', error);
    throw new Error('Die Datei konnte nicht hochgeladen werden. Bitte versuchen Sie es später erneut.');
  }
}

// === Optional: Nur Upload (ohne Patch) ===
// Falls später mal ein Use-Case entsteht, wo nur Upload ohne Patch gewünscht ist.
// Aktuell nicht verwendet, aber als Referenz implementiert.
export async function uploadFile(file: File): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<HubSpotUploadResponse>(
      '/hubspot/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.fileId;
  } catch (error) {
    console.error('Fehler beim Upload:', error);
    throw new Error('Die Datei konnte nicht hochgeladen werden.');
  }
}

// === Optional: Nur Patch (ohne Upload) ===
// Falls später mal ein Use-Case entsteht, wo nur Patch ohne Upload gewünscht ist.
// Z.B. zum Verknüpfen einer bereits hochgeladenen Datei.
export async function patchDeal(
  dealId: string,
  propertyName: string,
  fileId: string
): Promise<void> {
  try {
    await api.patch('/hubspot/deal', {
      dealId,
      fileId,
      property: propertyName
    });
  } catch (error) {
    console.error('Fehler beim Patch:', error);
    throw new Error('Der Deal konnte nicht aktualisiert werden.');
  }
}

export async function getDealProperty(dealId: string, property: string): Promise<string | null> {
  try {
    // api-Interceptor gibt bereits response.data zurück
    const body = await api.get<{ properties: Record<string, string> }>(
      `/hubspot/deal/${dealId}?property=${property}`
    ) as unknown as { properties: Record<string, string> };
    console.log('[getDealProperty] alle Properties:', JSON.stringify(body.properties, null, 2));
    console.log('[getDealProperty] gesuchter Key:', property, '→ Wert:', body.properties?.[property]);
    return body.properties?.[property] ?? null;
  } catch (error) {
    console.error('[getDealProperty] Fehler:', error);
    return null;
  }
}

export async function updateDealTextProperty(
  dealId: string,
  property: string,
  value: string
): Promise<void> {
  try {
    await api.patch('/hubspot/deal/property', { dealId, property, value });
  } catch (error) {
    console.error('Fehler beim Text-Property-Update:', error);
    // Die Funktion ist generisch für beliebige Text-Properties.
    // Deshalb muss auch die Fehlermeldung allgemein bleiben und darf
    // nicht auf einen speziellen Use-Case wie "Seriennummer" fest verdrahtet sein.
    throw new Error('Die Eigenschaft konnte nicht gespeichert werden.');
  }
}
